import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Bell, Handshake, MoreVertical } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { CrmShell, Modal, PageHeader } from '../../components/CrmShell';
import { EmptyState, formatCompactMoney, formatDate, formatMoney } from '../../components/CrmForms';
import { Activity, AppUser, Contact, Deal, Reminder } from '../../types';
import {
  deleteReminder,
  subscribeToContacts,
  subscribeToDeals,
  subscribeToPipelineSettings,
  subscribeToRecentActivities,
  subscribeToReminders,
  updateReminder,
} from '../../services/crm';
import { fetchUsers } from '../../services/users';
import { logGlobalError } from '../../utils/globalErrorLogger';
import { ReminderForm } from '../ContactsPage';

export function LandingPage() {
  const { currentUser, canAccessApp } = useAuth();

  if (!canAccessApp) {
    return <AccessRequiredPage />;
  }

  return <Dashboard currentUserId={currentUser?.uid ?? ''} />;
}

function Dashboard({ currentUserId }: { currentUserId: string }) {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [stages, setStages] = useState<string[]>([]);
  const [users, setUsers] = useState<AppUser[]>([]);
  const [editingReminder, setEditingReminder] = useState<Reminder | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubs = [
      subscribeToDeals(setDeals, (err) => setError(err.message)),
      subscribeToContacts(setContacts, (err) => setError(err.message)),
      subscribeToRecentActivities(setActivities, (err) => setError(err.message)),
      subscribeToReminders(setReminders, (err) => setError(err.message)),
      subscribeToPipelineSettings((settings) => setStages(settings.stages), (err) => setError(err.message)),
    ];
    fetchUsers().then(setUsers).catch((err) => {
      setError(err.message);
      logGlobalError(err, { context: 'Dashboard.fetchUsers' });
    });
    return () => unsubs.forEach((unsub) => unsub());
  }, []);

  const openDeals = deals.filter((deal) => !['Closed Won', 'Closed Lost'].includes(deal.stage));
  const totalOpen = openDeals.reduce((sum, deal) => sum + deal.value, 0);
  const stageData = stages.map((stage) => ({
    stage,
    value: deals.filter((deal) => deal.stage === stage).reduce((sum, deal) => sum + deal.value, 0),
  }));
  const ownerData = users
    .map((user) => ({
      owner: user.displayName || user.email || 'Unknown',
      count: openDeals.filter((deal) => deal.ownerId === user.uid).length,
    }))
    .filter((row) => row.count > 0);
  const month = new Date().getMonth();
  const year = new Date().getFullYear();
  const closedThisMonth = deals.filter((deal) =>
    ['Closed Won', 'Closed Lost'].includes(deal.stage)
    && deal.updatedAt.getMonth() === month
    && deal.updatedAt.getFullYear() === year,
  );
  const myReminders = reminders
    .filter((reminder) => !reminder.completed && reminder.assignedTo === currentUserId)
    .slice(0, 8);
  const contactsById = new Map(contacts.map((contact) => [contact.id, contact]));
  const dealsById = new Map(deals.map((deal) => [deal.id, deal]));
  const usersById = new Map(users.map((user) => [user.uid, user]));

  async function completeReminder(reminder: Reminder) {
    await updateReminder(reminder.id, { completed: true });
  }

  async function removeReminder(reminder: Reminder) {
    if (!window.confirm('Delete this reminder?')) return;
    await deleteReminder(reminder.id);
  }

  async function saveReminder(values: { assignedTo: string; dueDate: Date; note: string }) {
    if (!editingReminder) return;
    const id = editingReminder.id;
    setEditingReminder(null);
    try {
      await updateReminder(id, values);
    } catch (err: any) {
      setError(err?.message || 'Failed to save reminder');
      logGlobalError(err, { context: 'Dashboard.saveReminder' });
    }
  }

  return (
    <CrmShell>
      <PageHeader
        title="Dashboard"
        description="Pipeline, reminders, and team activity at a glance."
      />
      {error && <div className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      <div className="grid gap-4 md:grid-cols-4">
        <Metric label="Total open pipeline" value={formatMoney(totalOpen)} />
        <Metric label="Open deals" value={String(openDeals.length)} />
        <Metric label="Closed won this month" value={String(closedThisMonth.filter((d) => d.stage === 'Closed Won').length)} />
        <Metric label="Closed lost this month" value={String(closedThisMonth.filter((d) => d.stage === 'Closed Lost').length)} />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1.4fr_0.8fr]">
        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-900">Pipeline value by stage</h2>
          <div className="mt-4 h-72">
            {stageData.some((row) => row.value > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stageData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="stage" />
                  <YAxis tickFormatter={(value) => formatCompactMoney(Number(value))} width={56} />
                  <Tooltip formatter={(value) => formatMoney(Number(value))} />
                  <Bar dataKey="value" fill="#25286a" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState>No open pipeline yet.</EmptyState>
            )}
          </div>
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            <h2 className="text-sm font-semibold text-gray-900">My reminders</h2>
          </div>
          <div className="mt-4 space-y-3">
            {myReminders.length ? myReminders.map((reminder) => (
              <ReminderCard
                key={reminder.id}
                reminder={reminder}
                contactsById={contactsById}
                onDone={() => completeReminder(reminder)}
                onEdit={() => setEditingReminder(reminder)}
                onDelete={() => removeReminder(reminder)}
              />
            )) : <p className="text-sm text-gray-500">No upcoming reminders assigned to you.</p>}
          </div>
        </section>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-900">Deals by owner</h2>
          <div className="mt-4 space-y-2">
            {ownerData.length ? ownerData.map((row) => (
              <div key={row.owner} className="flex items-center justify-between text-sm">
                <span className="text-gray-700">{row.owner}</span>
                <span className="font-semibold text-gray-900">{row.count}</span>
              </div>
            )) : <p className="text-sm text-gray-500">No assigned open deals.</p>}
          </div>
        </section>
        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-2">
            <Handshake className="h-5 w-5 text-primary" />
            <h2 className="text-sm font-semibold text-gray-900">Quick links</h2>
          </div>
          <div className="mt-4 grid gap-2 sm:grid-cols-3">
            <QuickLink to="/contacts" label="Contacts" />
            <QuickLink to="/companies" label="Companies" />
            <QuickLink to="/deals" label="Pipeline" />
          </div>
        </section>
      </div>

      <ActivityInbox
        activities={activities}
        contactsById={contactsById}
        dealsById={dealsById}
        usersById={usersById}
      />
      {editingReminder && (
        <Modal title="Edit reminder" onClose={() => setEditingReminder(null)}>
          <ReminderForm
            onSubmit={saveReminder}
            currentUserId={currentUserId}
            initialValues={{
              assignedTo: editingReminder.assignedTo,
              dueDate: editingReminder.dueDate,
              note: editingReminder.note,
            }}
          />
        </Modal>
      )}
    </CrmShell>
  );
}

function ActivityInbox({
  activities,
  contactsById,
  dealsById,
  usersById,
}: {
  activities: Activity[];
  contactsById: Map<string, Contact>;
  dealsById: Map<string, Deal>;
  usersById: Map<string, AppUser>;
}) {
  return (
    <section className="mt-6 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-gray-900">Recent activity inbox</h2>
        <span className="text-xs font-medium text-gray-500">{activities.length} items</span>
      </div>
      <div className="mt-4 h-[44rem] overflow-y-auto overscroll-contain rounded-md border border-gray-200">
        {activities.length ? activities.map((activity) => {
          const contact = contactsById.get(activity.contactId);
          const deal = activity.dealId ? dealsById.get(activity.dealId) : undefined;
          const user = usersById.get(activity.loggedBy);
          const contactName = contact ? `${contact.firstName} ${contact.lastName}`.trim() || contact.email : 'Unknown contact';
          const loggerName = user?.displayName || user?.email || 'Unknown user';

          return (
            <article key={activity.id} className="border-b border-gray-100 px-4 py-3 last:border-b-0">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium uppercase tracking-wide text-gray-500">
                      {activity.type.replace('_', ' ')}
                    </span>
                    <Link className="text-sm font-semibold text-primary hover:underline" to={`/contacts/${activity.contactId}`}>
                      {contactName}
                    </Link>
                    {deal && (
                      <Link className="text-xs font-medium text-gray-500 hover:text-primary hover:underline" to={`/deals/${deal.id}`}>
                        {deal.name}
                      </Link>
                    )}
                  </div>
                  <div className="mt-1 text-sm font-medium text-gray-900">{activity.subject || activity.type.replace('_', ' ')}</div>
                  {activity.body && <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-gray-600">{activity.body}</p>}
                </div>
                <div className="shrink-0 text-left text-xs text-gray-500 sm:text-right">
                  <div>{formatDate(activity.activityDate)}</div>
                  <div className="mt-1">{loggerName}</div>
                </div>
              </div>
            </article>
          );
        }) : (
          <div className="p-8">
            <EmptyState>No recent activity yet.</EmptyState>
          </div>
        )}
      </div>
    </section>
  );
}

function AccessRequiredPage() {
  return (
    <CrmShell>
      <main className="mx-auto max-w-2xl py-16">
        <section className="rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm">
          <h1 className="text-xl font-bold text-gray-900">Access required</h1>
          <p className="mt-2 text-sm leading-6 text-gray-600">Contact the Aligned Admin for access.</p>
        </section>
      </main>
    </CrmShell>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-2 text-2xl font-bold text-gray-900">{value}</div>
    </section>
  );
}

function QuickLink({ to, label }: { to: string; label: string }) {
  return (
    <Link to={to} className="rounded-md border border-gray-200 bg-white px-3 py-2 text-center text-sm font-medium text-gray-700 hover:bg-gray-50">
      {label}
    </Link>
  );
}

function ReminderCard({
  reminder,
  contactsById,
  onDone,
  onEdit,
  onDelete,
}: {
  reminder: Reminder;
  contactsById: Map<string, Contact>;
  onDone: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  function runAction(action: () => void) {
    setMenuOpen(false);
    action();
  }

  const contact = reminder.entityType === 'contact' ? contactsById.get(reminder.entityId) : undefined;
  const contactName = contact ? `${contact.firstName} ${contact.lastName}`.trim() || contact.email : null;
  const linkText = reminder.entityType === 'contact' && contactName ? contactName : `View ${reminder.entityType}`;

  return (
    <div className="relative rounded border border-gray-200 p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-gray-900">{formatDate(reminder.dueDate)}</div>
          <Link className="text-xs text-primary hover:underline" to={reminder.entityType === 'deal' ? `/deals/${reminder.entityId}` : `/contacts/${reminder.entityId}`}>
            {linkText}
          </Link>
        </div>
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            aria-label="Reminder actions"
            className="flex h-7 w-7 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <MoreVertical className="h-4 w-4" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 z-10 mt-1 w-32 overflow-hidden rounded-md border border-gray-200 bg-white py-1 shadow-lg">
              <button type="button" className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50" onClick={() => runAction(onDone)}>Done</button>
              <button type="button" className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50" onClick={() => runAction(onEdit)}>Edit</button>
              <button type="button" className="block w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50" onClick={() => runAction(onDelete)}>Delete</button>
            </div>
          )}
        </div>
      </div>
      <p className="mt-1 text-sm text-gray-600">{reminder.note || 'Follow up'}</p>
    </div>
  );
}
